export type BillingPeriod = 'monthly' | 'annual';
export type Currency = 'AUD' | 'USD';
export type PriceTier = 'pro' | 'businessPro';

// Helper to convert subscription tier to price tier
export const toPriceTier = (tier: string): PriceTier => {
  const map: { [key: string]: PriceTier } = {
    PRO: 'pro',
    BUSINESS_PRO: 'businessPro',
  };
  return map[tier] || 'pro';
};

interface PriceConfig {
  currency: Currency;
  symbol: string;
  priceId: {
    pro: {
      monthly: string;
      annual: string;
    };
    businessPro: {
      monthly: string;
      annual: string;
    };
  };
}

// Map countries to currencies and price configurations
const COUNTRY_CURRENCY_MAP: { [key: string]: PriceConfig } = {
  AU: {
    currency: 'AUD',
    symbol: 'A$',
    priceId: {
      pro: {
        monthly: import.meta.env.VITE_STRIPE_PRO_PRICE_ID_AUD_MONTHLY,
        annual: import.meta.env.VITE_STRIPE_PRO_PRICE_ID_AUD_ANNUAL,
      },
      businessPro: {
        monthly: import.meta.env.VITE_STRIPE_BUSINESS_PRO_PRICE_ID_AUD_MONTHLY,
        annual: import.meta.env.VITE_STRIPE_BUSINESS_PRO_PRICE_ID_AUD_ANNUAL,
      },
    },
  },
  US: {
    currency: 'USD',
    symbol: '$',
    priceId: {
      pro: {
        monthly: import.meta.env.VITE_STRIPE_PRO_PRICE_ID_USD_MONTHLY,
        annual: import.meta.env.VITE_STRIPE_PRO_PRICE_ID_USD_ANNUAL,
      },
      businessPro: {
        monthly: import.meta.env.VITE_STRIPE_BUSINESS_PRO_PRICE_ID_USD_MONTHLY,
        annual: import.meta.env.VITE_STRIPE_BUSINESS_PRO_PRICE_ID_USD_ANNUAL,
      },
    },
  },
  // For all other countries, use USD pricing
  DEFAULT: {
    currency: 'USD',
    symbol: '$',
    priceId: {
      pro: {
        monthly: import.meta.env.VITE_STRIPE_PRO_PRICE_ID_USD_MONTHLY,
        annual: import.meta.env.VITE_STRIPE_PRO_PRICE_ID_USD_ANNUAL,
      },
      businessPro: {
        monthly: import.meta.env.VITE_STRIPE_BUSINESS_PRO_PRICE_ID_USD_MONTHLY,
        annual: import.meta.env.VITE_STRIPE_BUSINESS_PRO_PRICE_ID_USD_ANNUAL,
      },
    },
  },
};

export const getCurrencyForCountry = (country: string): PriceConfig => {
  return COUNTRY_CURRENCY_MAP[country] || COUNTRY_CURRENCY_MAP.DEFAULT;
};

export const getStripePriceId = (
  country: string,
  tier: string,
  period: BillingPeriod
): string => {
  const config = getCurrencyForCountry(country);
  const priceTier = toPriceTier(tier);
  return config.priceId[priceTier][period];
};

// Get display price for a specific tier and period
export const getDisplayPrice = (
  country: string,
  tier: string,
  period: BillingPeriod
): { amount: number; symbol: string; currency: string } => {
  const config = getCurrencyForCountry(country);
  const priceTier = toPriceTier(tier);
  
  // Use AUD prices for AU users
  if (country === 'AU') {
    return {
      amount: PRICES.AUD[priceTier][period],
      symbol: config.symbol,
      currency: config.currency,
    };
  }

  // Use USD prices for US and other users
  const basePrice = PRICES.USD[priceTier][period];

  // If not AU or US, show converted USD prices
  if (!['AU', 'US'].includes(country)) {
    const exchangeRate = EXCHANGE_RATES[country] || 1;
    return {
      amount: Math.round(basePrice * exchangeRate * 100) / 100,
      symbol: config.symbol || '$',
      currency: 'USD', // Always show USD for non-AU/US users
    };
  }

  return {
    amount: basePrice,
    symbol: config.symbol,
    currency: config.currency,
  };
};

// Base prices in USD and AUD
const PRICES = {
  AUD: {
    pro: {
      monthly: 4.95,
      annual: 49.50,
    },
    businessPro: {
      monthly: 9.95,
      annual: 99.50,
    },
  },
  USD: {
    pro: {
      monthly: 4.95,
      annual: 49.50,
    },
    businessPro: {
      monthly: 9.95,
      annual: 99.50,
    },
  },
};

// Exchange rates for display purposes (only used for non-AU/US users)
const EXCHANGE_RATES: { [key: string]: number } = {
  EUR: 0.91,
  GBP: 0.79,
  NZD: 1.62,
};
